/**
 * Strip system-injected preamble blocks from a captured user-prompt text.
 *
 * The lexiun-plugin's chat-history WRITE-side strip
 * (`stripInjectedPreamble` in
 * `lexruntime/openclaw/extensions/lexiun-core/src/chat-history/hooks.ts`)
 * now peels the SAME generalized `^System:` block family — selection-context,
 * reassess (`<user_reassessment_guidance>`), @-mention (`<user_referenced_files>`),
 * and any future side-channel payload — so NEWLY-captured rows are clean at the
 * source (see the chat-history-strip-generalize handback). This SPA-side READ
 * strip is therefore no longer the PRIMARY line of defense, but it stays because:
 *   1. **Retroactive cleanup** — the write-side strip only protects rows captured
 *      AFTER it shipped; already-persisted polluted rows stay polluted in the
 *      store until aged out. Applying the same strip at render time hides them on
 *      the very next reload, without ever deleting user data
 *      (`feedback-never-delete-user-live-data` is HARD).
 *   2. **Defense in depth** — a future SPA might fire a new directive
 *      tag before the runtime strip is generalized to cover it; the
 *      generalized SPA strip catches all System:-prefixed blocks
 *      regardless of the inner XML tag.
 *
 * Patterns peeled from the TOP of the text, in order:
 *   1. **Contiguous `System: …` block** — any number of consecutive
 *      `^System: …\n` lines (one regex covers reassess +
 *      `<user_reassessment_guidance>` + `<user_referenced_files>` +
 *      `<user_selected_context>` + any future side-channel format,
 *      and handles MULTIPLE stacked enqueue_events back-to-back).
 *   2. **Sender (untrusted metadata) ```json …```** — the provenance
 *      block injected by `src/auto-reply/reply/inbound-meta.ts` (per
 *      commit `f7d00947`).
 *   3. **Leading `[Weekday YYYY-MM-DD HH:MM TZ]` timestamp** — the
 *      core inbound-timestamp prefix.
 *
 * If after stripping the remaining text is empty (degenerate case
 * where the captured content was ENTIRELY preamble), returns the
 * ORIGINAL text. Better to render the raw blob than a blank bubble
 * that hides the underlying anomaly from the operator.
 */
export function stripLexiunInjectedPreamble(text: string): string {
  let t = text;
  // 1. Generalized System:-block strip (any contiguous lines).
  //    Trailing `\s*` consumes the blank line between this block and the
  //    next preamble layer (e.g. the Sender JSON block) — without it the
  //    `^`-anchored Sender regex on the next line fails to match.
  const hadNotification = /<notification\b/.test(t);
  // R2 (async-agents ruling 2026-07-18) — LOCKSTEP with the server twin
  // (chat-history/hooks.ts::stripInjectedPreamble): hidden wake
  // `<notification kind="…">…</notification>` envelopes must never render,
  // including on history replay of turns persisted BEFORE the server-side fix.
  t = t.replace(
    /^System:[^\n]*<notification\b[^>]*>[\s\S]*?<\/notification>[^\n]*\n?/gm,
    "",
  );
  t = t.replace(/<notification\b[^>]*>[\s\S]*?<\/notification>\s*/g, "");
  t = t.replace(/^(?:System:[^\n]*\n?)+\s*/, "");
  // 2. Sender (untrusted metadata) JSON provenance block.
  t = t.replace(/^Sender \(untrusted metadata\):\s*```json[\s\S]*?```\s*/i, "");
  // 3. Leading inbound timestamp.
  t = t.replace(/^\[[A-Z][a-z]{2} \d{4}-\d{2}-\d{2} \d{2}:\d{2}(?::\d{2})? [A-Z]+\]\s*/, "");
  const stripped = t.trim();
  if (stripped.length > 0) return stripped;
  // A PURE-notification message renders NOTHING (hidden system note) —
  // never fall back to the raw envelope. Ordinary text keeps the legacy
  // return-original guard.
  return hadNotification ? "" : text;
}
